/**
 * HeaderBell — Campana de alertas en el header.
 * Se pinta en ROJO con un badge de conteo cuando hay pedidos de Cómputo / Proyecto
 * pendientes. Al hacer click muestra el listado con link a cada módulo.
 * El popover se renderiza con un Portal a document.body (fixed, fondo blanco
 * sólido, sombra y z-index alto) para NO heredar la transparencia del header glass.
 * Polling cada 60 s.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bell, Cpu, ClipboardList, Award, Clock, Package, Wrench, Banknote, ListTodo } from "lucide-react";
import { API } from "../contexts/AuthContext";

const opts = { withCredentials: true };
const POLL_MS = 60000;

export default function HeaderBell() {
  const [data, setData] = useState({ total: 0, proyectos_nuevos: 0, computos_nuevos: 0, items: [] });
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, right: 0 });
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const navigate = useNavigate();

  const fetchAlertas = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/alertas/pendientes`, opts);
      setData(data || { total: 0, items: [] });
    } catch (_e) { /* silencioso */ }
  }, []);

  useEffect(() => {
    fetchAlertas();
    const id = setInterval(fetchAlertas, POLL_MS);
    return () => clearInterval(id);
  }, [fetchAlertas]);

  // Calcula la posición del popover a partir del botón (fixed, anclado).
  const positionPopover = useCallback(() => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setCoords({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) });
  }, []);

  const toggle = () => {
    setOpen((o) => {
      const next = !o;
      if (next) positionPopover();
      return next;
    });
  };

  // Cerrar al click fuera (botón o popover), y reposicionar en scroll/resize.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onReflow = () => positionPopover();
    document.addEventListener("mousedown", onClick);
    window.addEventListener("resize", onReflow);
    window.addEventListener("scroll", onReflow, true);
    return () => {
      document.removeEventListener("mousedown", onClick);
      window.removeEventListener("resize", onReflow);
      window.removeEventListener("scroll", onReflow, true);
    };
  }, [open, positionPopover]);

  const hasAlerts = (data.total || 0) > 0;

  const goTo = (item) => {
    setOpen(false);
    if (item.tipo === "certificacion") { navigate(`/obra/${item.obra_id}`); return; }
    if (item.tipo === "alquiler_vencimiento") { navigate("/compras/servicios"); return; }
    if (item.tipo === "material_uso_vencimiento") { navigate("/compras/pedidos"); return; }
    if (item.tipo === "mantenimiento_herramienta") { navigate("/inventario/herramientas"); return; }
    // Fix CJ (2026-09-11): vencimientos de Cuentas por Pagar / por Cobrar.
    if (item.tipo === "cxp_vencimiento") { navigate("/tesoreria/pagos"); return; }
    if (item.tipo === "cxc_vencimiento") { navigate("/tesoreria/cobranzas"); return; }
    // Fix CM (2026-09-13): la tarea abre su enlace directo (o la pantalla de tareas).
    if (item.tipo === "tarea") { navigate(item.tarea_id ? `/tareas/${item.tarea_id}` : "/tareas"); return; }
    // proyecto → módulo Proyecto (/ingenieria/computo); computo → módulo Cómputo (/ingenieria/proyecto)
    navigate(item.tipo === "proyecto" ? "/ingenieria/computo" : "/ingenieria/proyecto");
  };

  return (
    <div className="flex-shrink-0">
      <button
        ref={btnRef}
        onClick={toggle}
        className={`relative p-2 rounded-xl transition-all ${
          hasAlerts ? "text-red-500 hover:bg-red-50" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
        }`}
        data-testid="notifications-btn"
        title={hasAlerts ? `${data.total} pedido(s) pendiente(s)` : "Sin pendientes"}
      >
        <Bell size={18} className={hasAlerts ? "animate-pulse" : ""} fill={hasAlerts ? "currentColor" : "none"} />
        {hasAlerts && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center ring-2 ring-white"
            data-testid="notifications-badge"
          >
            {data.total > 9 ? "9+" : data.total}
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          ref={popRef}
          style={{ position: "fixed", top: coords.top, right: coords.right, backgroundColor: "#ffffff", zIndex: 9999 }}
          className="w-80 max-w-[90vw] rounded-2xl border border-gray-200 shadow-2xl overflow-hidden"
          data-testid="notifications-popover"
        >
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-white">
            <span className="text-[13px] font-bold text-gray-800">Pedidos pendientes</span>
            <span className="text-[11px] font-semibold text-red-500">{data.total} nuevo(s)</span>
          </div>
          {(data.items?.length || 0) === 0 ? (
            <div className="px-4 py-8 text-center text-[12.5px] text-gray-400 bg-white">
              No hay pedidos pendientes 🎉
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto divide-y divide-gray-50 bg-white">
              {data.items.map((it) => (
                <button
                  key={it._id}
                  onClick={() => goTo(it)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left transition-colors"
                  data-testid={`notification-item-${it._id}`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    it.tipo === "proyecto" ? "bg-violet-100 text-violet-600" : it.tipo === "certificacion" ? "bg-amber-100 text-amber-600" : it.tipo === "tarea" ? (it.status === "vencida" ? "bg-red-100 text-red-600" : "bg-emerald-100 text-emerald-600") : (it.tipo === "alquiler_vencimiento" || it.tipo === "material_uso_vencimiento" || it.tipo === "mantenimiento_herramienta" || it.tipo === "cxp_vencimiento" || it.tipo === "cxc_vencimiento") ? (it.status === "vencido" ? "bg-red-100 text-red-600" : "bg-orange-100 text-orange-600") : "bg-blue-100 text-blue-600"
                  }`}>
                    {it.tipo === "proyecto" ? <ClipboardList size={15} /> : it.tipo === "certificacion" ? <Award size={15} /> : it.tipo === "tarea" ? <ListTodo size={15} /> : it.tipo === "material_uso_vencimiento" ? <Package size={15} /> : it.tipo === "mantenimiento_herramienta" ? <Wrench size={15} /> : it.tipo === "alquiler_vencimiento" ? <Clock size={15} /> : (it.tipo === "cxp_vencimiento" || it.tipo === "cxc_vencimiento") ? <Banknote size={15} /> : <Cpu size={15} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[12.5px] font-semibold text-gray-800 truncate">
                      {it.budget_nombre || it.budget_number || "Pedido"}
                    </p>
                    <p className="text-[11px] text-gray-400 truncate">
                      {it.tipo === "proyecto" ? "Proyecto" : it.tipo === "certificacion" ? "Certificación pendiente"
                        : it.tipo === "alquiler_vencimiento" ? (it.status === "vencido" ? `Alquiler vencido · ${it.budget_number}` : `Alquiler vence en ${it.dias_restantes} día(s) · ${it.budget_number}`)
                        : it.tipo === "material_uso_vencimiento" ? (it.status === "vencido" ? "Retirar sobrante de material — vencido" : `Retirar sobrante de material en ${it.dias_restantes} día(s)`)
                        : it.tipo === "mantenimiento_herramienta" ? (it.status === "vencido" ? "Mantenimiento de herramienta vencido" : `Mantenimiento de herramienta en ${it.dias_restantes} día(s)`)
                        : it.tipo === "cxp_vencimiento" ? (it.status === "vencido" ? `Pago vencido hace ${Math.abs(it.dias_restantes)} día(s) · ${it.budget_number}` : `Pago vence ${it.dias_restantes === 0 ? "hoy" : `en ${it.dias_restantes} día(s)`} · ${it.budget_number}`)
                        : it.tipo === "cxc_vencimiento" ? (it.status === "vencido" ? `Cobro vencido hace ${Math.abs(it.dias_restantes)} día(s) · ${it.budget_number}` : `Cobro vence ${it.dias_restantes === 0 ? "hoy" : `en ${it.dias_restantes} día(s)`} · ${it.budget_number}`)
                        : it.tipo === "tarea" ? (it.status === "vencida" ? `Tarea vencida${it.budget_number ? ` · ${it.budget_number}` : ""}` : it.status === "frenada" ? `Tarea frenada +48 h${it.budget_number ? ` · ${it.budget_number}` : ""}` : `Tarea pendiente${it.budget_number ? ` · ${it.budget_number}` : ""}`)
                        : "Cómputo"} · {it.cliente || "—"}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
